import { css } from '@emotion/core';
import t from 'react-translate';
import React,
{ useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

// redux
import {
  editQuizQuestion,
} from 'redux/actions/quizzes';
import { ComponentType } from 'redux/schemas/models/lecture-component';

import {
  gray2,
  gray3,
  gray5,
  primary,
} from 'styles/global_defaults/colors';
import {
  quarterSpacing,
} from 'styles/global_defaults/scaffolding';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import QuestionSetContext, { SavingIndicator } from 'lecture_pages/services/question-set-context';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import { isEmpty } from 'underscore';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { config } from '@config/config.json';
import AddRowButton from './add-row-button';
import AddColumnButton from './add-column-button';
import DeleteRowButton from './delete-row-button';
import DeleteColumnButton from './delete-column-button';
import TableCell from './table-cell';

const getTableArray = (rows: number, columns: number, array: number[]) => {
  /**
   * This function will convert an array of elements to 2-D array according
   * to number of columns and rows
   *
   * eg:
   * input -> array=[1,2,3,4,5,6], columns=2, row=3,
   * output -> [[1,2],[3,4],[5,6]]
   */
  const a = [];

  for (let i = 0; i < rows; i += 1) {
    const n = i * columns;
    a[i] = []; // Initialize row array

    for (let j = 0; j < columns; j += 1) {
      a[i].push(array[j + n]);
    }
  }
  return a;
};

const TableQuestion = () => {
  const {
    currentQuestion,
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = useContext(QuestionSetContext);
  const { registerSaving } = React.useContext(SavingRegistryContext);
  const { numRows, numColumns, childrenQuestions } = currentQuestion;
  const { isEditMode, isCurrentActivityType, getQuestionLabelElementId } = useQuestionSetHelpers();
  const dispatch = useDispatch();

  const [childQuestions, setChildQuestions] = useState(getTableArray(numRows, numColumns, childrenQuestions));
  const [showButtons, setShowButtons] = useState(false);
  const [tableHeader, setTableHeader] = useState(currentQuestion.horizontalAxisLabel);
  const [isAddColumnDisabled, setIsAddColumnDisabled] = useState(false);
  const [isAddRowDisabled, setIsAddRowDisabled] = useState(false);
  const [isDeleteColumnDisabled, setIsDeleteColumnDisabled] = useState(false);
  const [isDeleteRowDisabled, setIsDeleteRowDisabled] = useState(false);

  const [rowIndex, setRowIndex] = useState(0);
  const [columnIndex, setColumnIndex] = useState(0);

  // position of cell hovered with respect to the position of table.
  const [cellX, setCellX] = useState(0);
  const [cellY, setCellY] = useState(0);

  // width and height of cell hovered.
  const [cellWidth, setCellWidth] = useState(0);
  const [cellHeight, setCellHeight] = useState(0);

  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    const node = tableContainerRef.current;
    if (!node) {
      return;
    }

    // To display add, delete column and row buttons on cell focus for keyboard accessibility
    node.addEventListener('focusin', () => setShowButtons(true));
    node.addEventListener('focusout', (e) => {
      if (!node.contains(e.relatedTarget as Node)) {
        setShowButtons(false);
      }
    });
  }, []);

  const addRowRef = useRef(null);
  const addColumnRef = useRef(null);
  const deleteRowRef = useRef(null);
  const deleteColumnRef = useRef(null);

  const updateSize = useCallback(() => {
    if (tableRef.current && addRowRef.current && addColumnRef.current) {
      // width of add row button should equal to width of table
      addRowRef.current.style.width = `${tableRef.current.offsetWidth}px`;

      // height of column button should equal to height of table
      addColumnRef.current.style.height = `${tableRef.current.offsetHeight}px`;

      // height of a delete row button should equal to height of hovered row
      deleteRowRef.current.style.height = `${cellHeight}px`;

      // width of a delete column button should equal to width of hovered column
      deleteColumnRef.current.style.width = `${cellWidth}px`;

      // setting the position of delete column button corresponding to the column which is hovered
      deleteColumnRef.current.style.left = `${cellX}px`;

      // setting the position of delete row button corresponding to row which is hovered
      deleteRowRef.current.style.top = `${cellY}px`;
    }
  }, [cellHeight, cellWidth, cellX, cellY]);

  useEffect(() => {
    setChildQuestions(getTableArray(numRows, numColumns, childrenQuestions));
  }, [childrenQuestions, numRows, numColumns]);

  useEffect(() => {
    updateSize();

    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [childQuestions, showButtons, updateSize]);

  const handleMouseEnter = () => {
    setShowButtons(true);
  };

  const handleMouseLeave = () => {
    setShowButtons(false);
  };

  const handleCellHover = (e, j) => {
    // setting position of the cell which is hovered. Position of cell is set in respective of table position
    setCellX(e.currentTarget.getBoundingClientRect().x - tableRef.current.getBoundingClientRect().x);
    setCellY(e.currentTarget.getBoundingClientRect().y - tableRef.current.getBoundingClientRect().y);

    // setting height and width of the cell which is hovered
    setCellHeight(e.currentTarget.getBoundingClientRect().height);
    setCellWidth(e.currentTarget.getBoundingClientRect().width);

    setColumnIndex(j);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTableHeaderChange = useCallback(debounce((value) => {
    const unregister = registerSaving();
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    dispatch(editQuizQuestion({
      id: currentQuestion.id,
      patch: {
        horizontalAxisLabel: value,
      },
    })).then((res) => {
      if (isEmpty(res.error)) {
        setSavingStatus(SavingIndicator.SUCCESS_STATUS);
      } else {
        setSavingStatus(SavingIndicator.ERROR_STATUS);
      }
    }).finally(() => {
      unregister();
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  }, 500), []);

  const styles = css`
    tr,
    td {
      border: 1px solid ${gray3};
    }
    .table-container:hover {
      td {
        border-color: ${primary} !important;
      }
    }
    table {
      border-collapse: collapse;
      padding: ${quarterSpacing}px;
    }
    td {
      height: 50px;
      width: ${100 / numColumns}%;
    }
    .table-wrapper {
      margin-top: 45px;
    }
    .table-container {
      position: relative;
      width: calc(100% - 40px);
    }
    table {
      width: 100%;
    }
    .header-input-container {
      input {
        background-color: transparent;
        border-radius: ${quarterSpacing}px;
        height: 45px;
        border-color: ${gray5};
        ::placeholder {
          color: ${gray2};
        }
      }
      input:focus {
        border-color: ${primary};
      }
    }
    .incorrect-answer-info {
      border-top: 4px solid ${gray5};
    }
  `;
  if (!isEditMode) {
    return (
      <div css={styles} className='mb-5'>
        {currentQuestion?.horizontalAxisLabel && (
          <p className='mb-4 text-gray-1 semi-bold text-small'>{currentQuestion.horizontalAxisLabel}</p>
        )}
        <table className='table-question' aria-labelledby={getQuestionLabelElementId(currentQuestion.id)}>
          {childQuestions.map((eachRows, i) => (
            <tr>
              {eachRows.map((eachChildQuestionId, j) => (
                <td>
                  <TableCell
                    childrenQuestions={currentQuestion.childrenQuestions}
                    currentQuestionId={eachChildQuestionId}
                    tableQuestionId={currentQuestion.id}
                  />
                </td>
              ))}
            </tr>
          ))}
        </table>
      </div>
    );
  }

  return (
    <div css={styles}>
      <div className='header-input-container w-100'>
        <p className='mb-0 text-gray-1 text-small'>
          {isCurrentActivityType(ComponentType.SURVEY)
            ? t.SURVEYS.TABLE_QUESTION.TABLE_QUESTION_EDIT_LABEL()
            : t.QUIZZES.TABLE_QUESTION.TABLE_QUESTION_EDIT_LABEL()}
        </p>
        <NvTextInput
          value={tableHeader}
          onChange={({ target: { value } }) => { setTableHeader(value); handleTableHeaderChange(value); }}
          placeholder={t.QUIZZES.TABLE_QUESTION.TABLE_HEADER_PLACEHOLDER()}
          className='mt-4'
          data-qa={config.pendo.activities.quiz.questionModal.tableQuestion.setTableHeader}
        />
      </div>
      <div ref={tableContainerRef} className='table-wrapper d-flex flex-column align-items-center justify-content-center p-1' onMouseLeave={handleMouseLeave}>
        <div className='table-container' onMouseEnter={handleMouseEnter}>
          {showButtons && (
            <React.Fragment>
              <DeleteColumnButton
                deleteColumnRef={deleteColumnRef}
                setShowButtons={setShowButtons}
                childQuestions={childQuestions}
                setChildQuestions={setChildQuestions}
                getTableArray={getTableArray}
                columnIndex={columnIndex}
                isDeleteColumnDisabled={isDeleteColumnDisabled}
                setIsDeleteColumnDisabled={setIsDeleteColumnDisabled}
              />
              <AddColumnButton
                addColumnRef={addColumnRef}
                isAddColumnDisabled={isAddColumnDisabled}
                setIsAddColumnDisabled={setIsAddColumnDisabled}
              />
              <AddRowButton
                addRowRef={addRowRef}
                isAddRowDisabled={isAddRowDisabled}
                setIsAddRowDisabled={setIsAddRowDisabled}
              />
              <DeleteRowButton
                deleteRowRef={deleteRowRef}
                setShowButtons={setShowButtons}
                childQuestions={childQuestions}
                setChildQuestions={setChildQuestions}
                getTableArray={getTableArray}
                rowIndex={rowIndex}
                isDeleteRowDisabled={isDeleteRowDisabled}
                setIsDeleteRowDisabled={setIsDeleteRowDisabled}
              />
            </React.Fragment>
          )}
          <table ref={tableRef} className='table-question' aria-labelledby={getQuestionLabelElementId(currentQuestion.id)}>
            {childQuestions.map((eachRows, i) => (
              <tr onMouseEnter={() => setRowIndex(i)}>
                {eachRows.map((eachChildQuestionId, j) => (
                  <td
                    onFocus={((e) => handleCellHover(e, j))}
                    onMouseEnter={(e) => handleCellHover(e, j)}
                  >
                    <TableCell
                      childrenQuestions={currentQuestion.childrenQuestions}
                      currentQuestionId={eachChildQuestionId}
                      tableQuestionId={currentQuestion.id}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableQuestion;
